import { IMG } from "./ui";

export type Post = {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  date: string; // human-readable
  iso: string; // ISO date for metadata
  read: string;
  image: string;
  author: string;
  /** Body blocks: heading or paragraph. */
  body: { type: "h2" | "p"; text: string }[];
};

export const POSTS: Post[] = [
  {
    slug: "why-we-let-email-do-the-scheduling",
    title: "Why we let email do the scheduling",
    excerpt:
      "Calendars are where hiring momentum goes to die. Here's how we turned a plain-text email reply into a fully automated scheduling system.",
    category: "Product",
    date: "May 28, 2026",
    iso: "2026-05-28",
    read: "6 min read",
    image: IMG.mist,
    author: "The WonderfulInterview team",
    body: [
      { type: "p", text: "Every extra step between an open role and a completed interview leaks candidates. Scheduling tools ask people to pick from a grid, create an account, or install yet another app. Each one is a place to drop off." },
      { type: "h2", text: "The simplest interface is the one people already use" },
      { type: "p", text: "When we invite a candidate, the email's reply-to routes straight back into the platform. The candidate just answers in plain English — \"Tuesday afternoon works\" or \"anytime after the 5th\" — exactly as they would to a human recruiter." },
      { type: "p", text: "An inbound webhook delivers that reply to our API, where a fast Claude model parses the free-text intent into a concrete slot. If it's unambiguous we confirm it; if not, we propose the nearest options. The candidate never sees a calendar widget." },
      { type: "h2", text: "Locking the slot — and the link" },
      { type: "p", text: "Once a time is agreed, the invitation moves to scheduled and we mint a single-use join link that's only valid inside the interview window. A reminder goes out automatically before the session, and stale invitations expire on their own." },
      { type: "p", text: "The result is a scheduling system with zero surface area for the candidate: no login, no app, no back-and-forth with a human. Just one email, answered naturally." },
    ],
  },
  {
    slug: "scoring-an-interview-without-bias",
    title: "Scoring an interview without bias",
    excerpt:
      "A look inside the rubric-driven evaluation pipeline: how dimension scores, strengths and concerns are produced consistently for every candidate.",
    category: "Engineering",
    date: "May 14, 2026",
    iso: "2026-05-14",
    read: "8 min read",
    image: IMG.lake,
    author: "The WonderfulInterview team",
    body: [
      { type: "p", text: "Consistency is the hardest thing to get right in interviewing. Two humans rarely score the same answer the same way, and the same human drifts across a long day of calls. Our evaluation pipeline is built to remove that drift." },
      { type: "h2", text: "Start with an explicit rubric" },
      { type: "p", text: "Every interview template carries a rubric: a set of weighted dimensions with labels and descriptions. The evaluator scores the transcript against those exact dimensions, every time, so the same criteria apply to every candidate for that role." },
      { type: "h2", text: "Structured output, not vibes" },
      { type: "p", text: "The model returns a structured evaluation: an overall score from 0–100, per-dimension scores with a written rationale, a short summary, explicit strengths and concerns, and a recommendation from strong yes to strong no. Because the shape is fixed, reports are directly comparable side by side." },
      { type: "h2", text: "Evidence over opinion" },
      { type: "p", text: "Each rationale points back to what the candidate actually said. Integrity signals are attached as evidence rather than verdicts. The platform never silently fails anyone — it surfaces the reasoning and lets a human make the call." },
    ],
  },
  {
    slug: "five-layers-of-interview-integrity",
    title: "Five layers of interview integrity",
    excerpt:
      "Perception signals, client telemetry, single-use tokens, transcript analysis and recordings — and why we surface evidence instead of silently failing people.",
    category: "Trust & Safety",
    date: "Apr 30, 2026",
    iso: "2026-04-30",
    read: "7 min read",
    image: IMG.hero,
    author: "The WonderfulInterview team",
    body: [
      { type: "p", text: "Remote interviews invite a familiar worry: how do you know the person you scored is the person who showed up, answering on their own? Our answer is layered, and deliberately transparent." },
      { type: "h2", text: "1. Perception signals" },
      { type: "p", text: "The video model emits perception events during the session — gaze leaving the screen, more than one face in frame, or no face at all. Each arrives with a severity." },
      { type: "h2", text: "2. Client telemetry" },
      { type: "p", text: "The candidate's browser reports page-blur counts, paste events and fullscreen exits. None of these are damning on their own, but together they add context." },
      { type: "h2", text: "3. Single-use, time-boxed access" },
      { type: "p", text: "The join link is single-use and only valid inside the scheduled window. One candidate, one window, one link." },
      { type: "h2", text: "4. Transcript analysis" },
      { type: "p", text: "The evaluator flags reading-aloud cadence, prompt-injection attempts and third-party coaching cues directly from the transcript." },
      { type: "h2", text: "5. Recordings" },
      { type: "p", text: "Every session is captured to private storage and served only through signed links, as a human-review fallback. All five signals merge into the report as evidence — never as an automatic rejection." },
    ],
  },
  {
    slug: "pay-as-you-go-explained",
    title: "Pay-as-you-go, explained",
    excerpt:
      "How our pricing works, why an interview costs what it costs, and how we keep it transparent down to the minute.",
    category: "Company",
    date: "Apr 18, 2026",
    iso: "2026-04-18",
    read: "4 min read",
    image: IMG.desk,
    author: "The WonderfulInterview team",
    body: [
      { type: "p", text: "Seat-based pricing punishes you for growing your team and charges you whether or not you interview anyone. We didn't want that, so WonderfulInterview is pay-as-you-go: you're billed per completed interview, and nothing else." },
      { type: "h2", text: "Priced by duration" },
      { type: "p", text: "Each interview's price is set by its length and locked when you invite the candidate — 15 minutes is $17, 30 minutes is $33.50, 45 minutes is $50.50, and 60 minutes is $67 on default rates. That one price covers the AI interviewer, the evaluation, scheduling and every candidate email." },
      { type: "h2", text: "You only pay when it completes" },
      { type: "p", text: "No-shows and incomplete sessions aren't billed. You add a card once — captured securely with no charge — and you're billed only when an interview is completed and evaluated. No seats, no subscription, no minimums." },
      { type: "p", text: "We quote the exact price before you send a single invite, so there are never surprises on the invoice." },
    ],
  },
  {
    slug: "designing-a-calm-ai-interviewer",
    title: "Designing a calm AI interviewer",
    excerpt:
      "Lifelike doesn't mean uncanny. The design principles behind an AI video interviewer candidates actually feel comfortable talking to.",
    category: "Design",
    date: "Apr 2, 2026",
    iso: "2026-04-02",
    read: "5 min read",
    image: IMG.office,
    author: "The WonderfulInterview team",
    body: [
      { type: "p", text: "An interview is already a stressful moment. An AI interviewer that feels cold or glitchy makes it worse — and a stressed candidate gives you a worse signal. So our north star was calm." },
      { type: "h2", text: "Adaptive, not scripted" },
      { type: "p", text: "The interviewer follows a question plan but asks real-time follow-ups, the way a good human would. That keeps the conversation feeling like a conversation, not a form read aloud." },
      { type: "h2", text: "No installs, no friction" },
      { type: "p", text: "Everything runs in the browser from a single link. The fewer hoops between the candidate and the conversation, the more natural they are when it starts." },
      { type: "p", text: "Lifelike is a means, not an end. The goal isn't to fool anyone — it's to make the moment relaxed enough that people show you their best." },
    ],
  },
  {
    slug: "running-on-the-edge",
    title: "Running on the edge",
    excerpt:
      "Why we built the whole platform on a global edge runtime, and what that means for latency, reliability and cost.",
    category: "Engineering",
    date: "Mar 20, 2026",
    iso: "2026-03-20",
    read: "9 min read",
    image: IMG.team2,
    author: "The WonderfulInterview team",
    body: [
      { type: "p", text: "WonderfulInterview runs on Cloudflare's edge: the API is a Workers application, the web app is served from Pages, and recordings live in R2. Here's why that combination earns its place." },
      { type: "h2", text: "Close to every candidate" },
      { type: "p", text: "Candidates join from everywhere. Running at the edge means the API answers from a location near them, with no cold starts to stall the first request." },
      { type: "h2", text: "Boring reliability" },
      { type: "p", text: "A relational database (Neon Postgres over an HTTP driver) keeps billing and usage correct, while idempotent, signature-verified webhooks make our integrations safe to retry. Scheduled jobs run every fifteen minutes to send reminders and expire stale invitations." },
      { type: "p", text: "The payoff is a platform that's fast for candidates, cheap to run, and global by default — without an ops team babysitting servers." },
    ],
  },
];

export function getPost(slug: string): Post | undefined {
  return POSTS.find((p) => p.slug === slug);
}
